
import React, { useState, useRef, useEffect } from "react";
import { photos } from "../../data/photos";

import Lightbox from "yet-another-react-lightbox";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/captions.css"; 

import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

function FadeIn({ children, direction = "left" }) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={
        direction === "left"
          ? `fade-in-left ${visible ? "visible" : ""}`
          : direction === "right"
          ? `fade-in-right ${visible ? "visible" : ""}`
          : direction === "bottom"
          ? `fade-in-bottom ${visible ? "visible" : ""}`
          : ""
      }
    >
      {children}
    </div>
  );
}

export default function PhotoGallerySection() {
  const scrollRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const cardWidth = 300 + 32;

  const checkScroll = () => {
    const node = scrollRef.current;
    if (!node) return;

    setAtStart(node.scrollLeft <= 2);
    setAtEnd(node.scrollLeft + node.offsetWidth >= node.scrollWidth - 2);

    const cards = Array.from(node.children);
    let closestIdx = 0;
    let minDiff = Infinity;

    cards.forEach((card, idx) => {
      const center = card.offsetLeft + card.offsetWidth / 2;
      const scrollCenter = node.scrollLeft + node.offsetWidth / 2;
      const diff = Math.abs(center - scrollCenter);
      if (diff < minDiff) {
        minDiff = diff;
        closestIdx = idx;
      }
    });
    
    setActiveIndex(closestIdx);
  };

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    // checkScroll();
    const handleScroll = () => requestAnimationFrame(checkScroll);
    node.addEventListener('scroll', handleScroll);
    return () => node.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollBy = (offset) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  const scrollToIndex = (index) => {
    const node = scrollRef.current;
    if (!node) return;

    const card = node.children[index];
    if (!card) return;

    const cardCenter = card.offsetLeft + card.offsetWidth / 2;
    const containerCenter = node.offsetWidth / 2;
    const targetScroll = cardCenter - containerCenter;

    node.scrollTo({
      left: targetScroll,
      behavior: "smooth",
    });
  };

  return (
    <section className="w-full mx-auto bg-white px-2 sm:px-6 py-16 flex flex-col md:flex-row gap-6 rounded-xl
              items-stretch relative overflow-visible">
      {/* <FadeIn direction="left"> */}

        <div 
              className="w-full md:w-1/3 min-w-[240px] z-10 flex flex-col justify-center bg-white 
              bg-opacity-70 rounded-xl md:pr-10 px-4 py-4 md:py-14 items-center md:items-start text-center
              md:text-left" >

          <h2 className="text-4xl font-extrabold text-sky-600">Photo Gallery</h2>
          <p className="hidden lg:block text-center text-gray-500 mb-6 text-sm py-4 ">
            Scroll/Click → to see more photos
          </p>
          <p className="text-center text-gray-500 mb-6 text-sm py-4 sm:hidden">
            Swipe/Click → to see more photos
          </p>
        </div>
      {/* </FadeIn> */}

      <div className="md:w-2/3 relative">
        
        <FadeIn direction="right">
          <div
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth flex gap-8 px-12 py-2"
            style={{ scrollSnapType: "x mandatory" }}
          >
            {photos.map((src, idx) => (
              <div
                key={src}
                className="min-w-[340px] sm:min-w-[360px] h-[480px] flex-shrink-0 bg-white bg-opacity-70 
                rounded-2xl border shadow-lg flex flex-col justify-end relative text-center 
                          p-4 select-none cursor-pointer"
                style={{ scrollSnapAlign: "center" }}
                onClick={() => {
                  setPhotoIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <div className=" w-full h-[480px] sm:h-[280px] md:h-[480px]">
                  <img
                    src={src}
                    alt={`Ambulance Image ${idx + 1}`}
                    className="absolute inset-0 w-full h-full object-cover rounded-xl"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:flex w-full flex justify-center items-center gap-8 py-6">
            <button
              type="button"
              aria-label="Scroll Left"
              disabled={atStart}
              onClick={() => scrollBy(-cardWidth)}
              className={`p-3 rounded-full bg-gray-100 bg-opacity-70 shadow hover:bg-opacity-90 transition ${
                atStart ? "opacity-40 cursor-not-allowed" : ""
              }`}
            >
              <FaChevronLeft className="text-sky-600 text-xl" />
            </button>
            <button
              type="button"
              aria-label="Scroll Right"
              disabled={atEnd}
              onClick={() => scrollBy(cardWidth)}
              className={`p-3 rounded-full bg-gray-100 bg-opacity-70 shadow hover:bg-opacity-90 transition ${
                atEnd ? "opacity-40 cursor-not-allowed" : ""
              }`}
            >
              <FaChevronRight className="text-sky-600 text-xl" />
            </button>
          </div>

          <div className="flex md:hidden justify-center gap-3 mt-6">
            {photos.map((_, idx) => (
              <button
                key={idx}
                className={`w-3 h-3 rounded-full transition ${
                  idx === activeIndex ? "bg-sky-600 scale-110" : "bg-gray-300"
                }`}
                aria-label={`Select photo ${idx + 1}`}
                onClick={() => scrollToIndex(idx)}
              />
            ))}
          </div>

          {lightboxOpen && (
            <Lightbox
              open={lightboxOpen}
              close={() => setLightboxOpen(false)}
              slides={photos.map((src) => ({ src }))}
              index={photoIndex}
              on={{
                view: ({ index }) => setPhotoIndex(index),
              }}
              plugins={[Fullscreen, Zoom, Slideshow, Thumbnails]}
            />
          )}
      </FadeIn>
      </div>
    </section>
  );
}
